import { DatasheetGetDto } from '@common/dto/datasheet/get-dto';
import { DatasheetTabDto } from '@common/dto/datasheet/tab-dto';
import * as React from 'react';
import { useDatasheetController } from './datasheet';
import { DatasheetUpnListDto } from '@common/dto/datasheet/upn/list';
import { useDatasheetPrintController } from '@modules/datasheets/controllers/datasheet-print';
import { PRINTABLE_RESULT_DATASHEET_ELEMENT_ID, PRINTABLE_DATASHEET_FIELD_ID } from '../../datasheet-printing-settings';
import { generateId } from '@common/utils/common';
import { FormatFieldItem } from '@common/components/format-field-selector';
import {
  GetDatasheetFolderFormats,
  getDatasheetResultFolderFormats as getDatasheetFolderFormatsBase,
} from "@modules/datasheets/services/formats";
import { trDts } from "@modules/datasheets/utils/i18n";
import { getDatasheetFolders } from "@modules/datasheets/services/folders";
import {
  acquireDatasheetLock,
  addDatasheetTableRow,
  getNotLoadedDatasheetComponents,
  heartbeatDatasheetLock,
  loadDatasheetData,
  loadDatasheetFormat,
  loadTableDatasheetData,
  LOAD_DS_FORMAT_TABLE_CONTEXT,
  releaseDatasheetLock,
} from "@modules/datasheets/services/datasheet";
import { mergeDatasheetFormatTextsWithDataTexts } from "@modules/datasheets/utils/merge-datasheet-format-texts-with-data-texts";

import { DatasheetFormatManagementFolder } from '../components/header/format-management.controller';
import {
  selectDatasheetFormatPreference,
  setDatasheetFormatPreference,
} from '../../../reducers/formatPreference';
import {
  selectDatasheetTabPreference,
  setDatasheetTabPreference,
} from '../../../reducers/tabPreference';

const DEFAULT_GRID_SETTINGS = {
  horizontalGrid: 30,
  verticalGrid: 30,
  showGrid: true,
  showPageBreaks: false,
  snapToGrid: true,
  showEditTabOrder: false,
  showMargins: false,
  marginWidth: 400,
  marginHeight: 300,
};

/** Passthrough: keep array order as received from BE. No sort, no dedupe. */
const parseRrnsPassthrough = (values: Array<number | string | null | undefined>): number[] => {
  return values
    .map((v) => Number(v))
    .filter((rrn) => Number.isFinite(rrn) && rrn > 0);
};

const lastFormatIdByContext: Record<string, number> = {};

/** Match GWT DatasheetTabWnd lock timer interval (30s). */
const DATASHEET_LOCK_HEARTBEAT_MS = 30_000;

export type ResultProps = {
  context?: number;
  formatId?: number;
  /** When non-zero, this tab is a table datasheet result (TDS); use context=8 for loadDSFormat. */
  tableFieldId?: number;
  tableContext?: number;
  /** Entity id (individual / pedigree id, depending on context) */
  globalId?: number;
  /** When true, show only the format picker in the header (split-view). */
  formatOnly?: boolean;
  /**
   * Optional grouping key used to scope the "temporary default" datasheet
   * format selection in Redux (e.g. per pedigree or per individual).
   */
  groupKey?: string;
  /**
   * Indicates whether the current user is allowed to set folder-level
   * default datasheet formats (admin, end-user admin, or superuser).
   * Passed from the GWT host via initDatasheetsResult.
   */
  canSetFolderDefault?: boolean;
  /**
   * True when folder Template Option (Individual/Pedigree Datasheet) is ON:
   * show only "Set as Default for this Folder". False: show only "Set as Default for this Pedigree".
   */
  templateOptionOn?: boolean;
  /**
   * Root folder id for the entity (IDS/PDS folder tree), passed from the GWT
   * host. Used when persisting folder-level default datasheet formats so we
   * update the correct folder, not the datasheet-format folder.
   */
  rootFolderId?: number;
  /**
   * Initial row relative number when opening from a parent table (e.g. "Open
   * Table Datasheet" on a selected row). The result tab will jump to this row.
   */
  initialRrn?: number;
  /** When changed (e.g. pedigree reopened), reload recently used formats from backend */
  reloadRecentlyUsedKey?: number | string;
};

export const useDatasheetResultMainController = (props: ResultProps) => {
  const isTableDatasheetResult = React.useMemo(
    () => props.tableFieldId != null && props.tableFieldId !== 0,
    [props.tableFieldId]
  );

  /** 1 = IDS (lock id = global id), 2 = PDS (lock id = family id); matches BE datasheet lock API {@code context}. */
  const datasheetLockContext = React.useMemo((): 1 | 2 | null => {
    if (isTableDatasheetResult) return null;
    if (props.context === 1 || props.context === 2) return props.context;
    return null;
  }, [isTableDatasheetResult, props.context]);

  const [datasheetLockHeld, setDatasheetLockHeld] = React.useState(false);
  const [datasheetLockBlocked, setDatasheetLockBlocked] = React.useState(false);
  const [datasheetLockUserName, setDatasheetLockUserName] = React.useState<string | null>(null);
  const datasheetHeldLockRef = React.useRef<{ id: number; context: number } | null>(null);
  const prevGlobalIdForLockRef = React.useRef<number | undefined>(undefined);

  const applyDatasheetLockState = React.useCallback(
    (params: { id: number; context: number; acquired: boolean; lockUser?: string | null }) => {
      if (params.acquired) {
        datasheetHeldLockRef.current = { id: params.id, context: params.context };
        setDatasheetLockHeld(true);
        setDatasheetLockBlocked(false);
        setDatasheetLockUserName(null);
      } else {
        datasheetHeldLockRef.current = null;
        setDatasheetLockHeld(false);
        setDatasheetLockBlocked(true);
        setDatasheetLockUserName(params.lockUser ?? null);
      }
    },
    []
  );

  const cacheKey = React.useMemo(() => {
    const ctx = props.context ?? "default";
    const group =
      props.groupKey ?? (props.globalId != null ? `id:${props.globalId}` : "global");
    const tdsSuffix = props.tableFieldId != null && props.tableFieldId !== 0 ? `|tds:${props.tableFieldId}` : "";
    return `context:${ctx}|group:${group}${tdsSuffix}`;
  }, [props.context, props.groupKey, props.globalId, props.tableFieldId]);

  const store = React.useMemo(() => {
    if (typeof window !== 'undefined') {
      return (window as any).__REDUX_STORE__?.datasheet || (window as any).__REDUX_STORE__?.latest || null;
    }
    return null;
  }, []);

  const getCachedFormatIdFromStore = React.useCallback(() => {
    if (store && typeof store.getState === 'function') {
      return selectDatasheetFormatPreference(store.getState(), cacheKey);
    }
    return undefined;
  }, [store, cacheKey]);

  const [cachedFormatId, setCachedFormatId] = React.useState<number | undefined>(() =>
    getCachedFormatIdFromStore()
  );

  React.useEffect(() => {
    if (!store || typeof store.subscribe !== 'function') {
      return;
    }
    return store.subscribe(() => {
      setCachedFormatId(getCachedFormatIdFromStore());
    });
  }, [store, getCachedFormatIdFromStore]);

  const [formatId, setFormatId] = React.useState<number | undefined>(() => {
    if (props.formatId && props.formatId > 0) {
      return props.formatId;
    }
    return cachedFormatId;
  });

  const [datasheet, setDatasheet] = React.useState<DatasheetGetDto>({
    id: 0,
    name: '',
    tabs: [],
    gridSettings: DEFAULT_GRID_SETTINGS,
  });

  const tabCacheKey = React.useMemo(() => {
    const ctx = props.context ?? 'default';
    const fmtId = datasheet.id || formatId || props.formatId || lastFormatIdByContext[String(ctx)] || 0;
    if (props.formatOnly === true && props.groupKey) {
      return `tab:split:${props.groupKey}:context:${ctx}:format:${fmtId}`;
    }
    return `tab:context:${ctx}:format:${fmtId}`;
  }, [props.context, props.formatOnly, props.groupKey, datasheet.id, formatId, props.formatId]);

  React.useEffect(() => {
    if (datasheet.id > 0 && props.context != null) {
      lastFormatIdByContext[String(props.context)] = datasheet.id;
    }
  }, [datasheet.id, props.context]);

  const getCachedTabIdFromStore = React.useCallback(() => {
    if (store && typeof store.getState === 'function') {
      return selectDatasheetTabPreference(store.getState(), tabCacheKey);
    }
    return undefined;
  }, [store, tabCacheKey]);

  const persistTabPreference = React.useCallback((tabId: number | undefined) => {
    if (store && tabCacheKey) {
      store.dispatch(setDatasheetTabPreference(tabCacheKey, tabId));
    }
  }, [store, tabCacheKey]);

  const [currentUpn, setCurrentUpn] = React.useState<DatasheetUpnListDto['id']>();
  const [showNoDefaultFormatDialog, setShowNoDefaultFormatDialog] = React.useState(() => {
    const effectiveFormatId = (props.formatId && props.formatId > 0) ? props.formatId : cachedFormatId;
    return !effectiveFormatId;
  });
  const [isLoadingData, setIsLoadingData] = React.useState(false);
  const [tdsCurrentRowIndex, setTdsCurrentRowIndex] = React.useState(0);
  const [tdsRowRrns, setTdsRowRrns] = React.useState<number[]>([]);
  const hasAppliedInitialTdsRowRef = React.useRef(false);
  const tdsUserPickedFormat = React.useRef(false);
  const preventTableDataPreservationRef = React.useRef(false);
  const tableSyncMarkerKey = React.useMemo(
    () => (props.globalId != null ? `DS_TABLE_SYNC_MARKER_${props.globalId}` : undefined),
    [props.globalId]
  );
  const seenTableSyncMarkerRef = React.useRef<string | null>(null);

  const handleUpnChange = React.useCallback((upn: DatasheetUpnListDto['id']) => {
    setCurrentUpn(upn);
  }, []);

  React.useEffect(() => {
    if (isTableDatasheetResult || datasheetLockContext == null || props.globalId == null || props.globalId <= 0) {
      const h = datasheetHeldLockRef.current;
      if (h) {
        releaseDatasheetLock(h).catch(() => {});
        datasheetHeldLockRef.current = null;
      }
      setDatasheetLockHeld(false);
      setDatasheetLockBlocked(false);
      setDatasheetLockUserName(null);
      prevGlobalIdForLockRef.current = undefined;
    }
  }, [isTableDatasheetResult, datasheetLockContext, props.globalId]);

  React.useEffect(() => {
    if (isTableDatasheetResult || datasheetLockContext == null || props.globalId == null || props.globalId <= 0) {
      return;
    }
    const prev = prevGlobalIdForLockRef.current;
    if (prev === undefined) {
      prevGlobalIdForLockRef.current = props.globalId;
      return;
    }
    if (prev === props.globalId) {
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        await releaseDatasheetLock({ id: prev, context: datasheetLockContext });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to release datasheet lock after entity change", err);
      }
      if (cancelled) return;
      datasheetHeldLockRef.current = null;
      try {
        const r = await acquireDatasheetLock({ id: props.globalId!, context: datasheetLockContext });
        if (cancelled) return;
        prevGlobalIdForLockRef.current = props.globalId;
        applyDatasheetLockState({
          id: props.globalId!,
          context: datasheetLockContext,
          acquired: r.acquired,
          lockUser: r.lockUser,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to acquire datasheet lock for entity", err);
        if (!cancelled) {
          applyDatasheetLockState({
            id: props.globalId!,
            context: datasheetLockContext,
            acquired: false,
            lockUser: null,
          });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [props.globalId, datasheetLockContext, isTableDatasheetResult, applyDatasheetLockState]);

  React.useEffect(() => {
    if (!datasheetLockHeld) return;
    const held = datasheetHeldLockRef.current;
    if (!held) return;
    const { id: lockEntityId, context: lockContext } = held;
    const send = () => {
      heartbeatDatasheetLock({ id: lockEntityId, context: lockContext }).catch((err) => {
        // eslint-disable-next-line no-console
        console.error("Datasheet lock heartbeat failed", err);
      });
    };
    send();
    const intervalId = window.setInterval(send, DATASHEET_LOCK_HEARTBEAT_MS);
    return () => window.clearInterval(intervalId);
  }, [datasheetLockHeld]);

  React.useEffect(() => {
    return () => {
      const h = datasheetHeldLockRef.current;
      if (h) {
        releaseDatasheetLock(h).catch(() => {});
        datasheetHeldLockRef.current = null;
      }
    };
  }, []);

  const tdsFormatIdFromStorage = React.useMemo(() => {
    if (!isTableDatasheetResult || !props.tableFieldId || props.globalId == null) return undefined;
    if (typeof sessionStorage === "undefined") return undefined;
    const key = `TDS_PENDING_FORMAT_ID_${props.tableFieldId}_${props.globalId}`;
    const raw = sessionStorage.getItem(key);
    if (raw == null) return undefined;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) || n <= 0 ? undefined : n;
  }, [isTableDatasheetResult, props.tableFieldId, props.globalId]);

  const tdsTabIdFromStorage = React.useMemo(() => {
    if (!isTableDatasheetResult || !props.tableFieldId || props.globalId == null) return undefined;
    if (typeof sessionStorage === "undefined") return undefined;
    const key = `TDS_PENDING_TAB_ID_${props.tableFieldId}_${props.globalId}`;
    const raw = sessionStorage.getItem(key);
    if (raw == null) return undefined;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) || n <= 0 ? undefined : n;
  }, [isTableDatasheetResult, props.tableFieldId, props.globalId]);

  const tdsRowRrnsFromStorage = React.useMemo(() => {
    if (!isTableDatasheetResult || !props.tableFieldId || props.globalId == null) return undefined;
    if (typeof sessionStorage === "undefined") return undefined;
    const key = `TDS_PENDING_ROW_RRNS_${props.tableFieldId}_${props.globalId}`;
    const raw = sessionStorage.getItem(key);
    if (!raw) return undefined;
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return undefined;
      return parseRrnsPassthrough(parsed as Array<number | string | null | undefined>);
    } catch {
      return undefined;
    }
  }, [isTableDatasheetResult, props.tableFieldId, props.globalId]);

  React.useEffect(() => {
    if (!isTableDatasheetResult) return;
    if (tdsRowRrns.length > 0) return;

    const selectedRrn =
      props.initialRrn != null && props.initialRrn > 0 ? props.initialRrn : undefined;
    const storageRows =
      tdsRowRrnsFromStorage && tdsRowRrnsFromStorage.length > 0 ? tdsRowRrnsFromStorage : [];
    const combinedRows =
      selectedRrn != null && !storageRows.includes(selectedRrn)
        ? [...storageRows, selectedRrn]
        : storageRows;
    const initialRows = parseRrnsPassthrough(combinedRows);
    if (initialRows.length > 0) {
      setTdsRowRrns(initialRows);
    }

    if (selectedRrn != null && initialRows.length > 0) {
      const selectedIdx = initialRows.findIndex((rrn) => rrn === selectedRrn);
      setTdsCurrentRowIndex(selectedIdx >= 0 ? selectedIdx : 0);
    } else {
      setTdsCurrentRowIndex(0);
    }
  }, [isTableDatasheetResult, tdsRowRrns, tdsRowRrnsFromStorage, props.initialRrn]);

  React.useEffect(() => {
    hasAppliedInitialTdsRowRef.current = false;
  }, [isTableDatasheetResult, datasheet.id, props.initialRrn]);

  React.useEffect(() => {
    const effectiveFormatId =
      (tdsFormatIdFromStorage != null && tdsFormatIdFromStorage > 0)
        ? tdsFormatIdFromStorage
        : (props.formatId != null && props.formatId > 0 ? props.formatId : undefined);
    if (!isTableDatasheetResult || !effectiveFormatId || effectiveFormatId <= 0) {
      return;
    }
    const tableContextForApi = props.tableContext ?? props.context ?? 1;
    const contextForApi = LOAD_DS_FORMAT_TABLE_CONTEXT;
    let cancelled = false;
    const parentTabIdForApi = tdsTabIdFromStorage ?? 1;
    loadDatasheetFormat({
      formatId: effectiveFormatId,
      context: contextForApi,
      tableContext: tableContextForApi,
      globalIdForTds: props.globalId,
      tabId: parentTabIdForApi,
    })
      .then((response) => {
        if (cancelled) return;
        const format = (response as any)?.format ?? response;
        if (!format || !format.tabsData) return;
        const tabs = format.tabsData.map((tab: any, idx: number) => ({
          id: tab.id ?? idx + 1,
          name: tab.name ?? `Tab ${idx + 1}`,
          texts: tab.texts || [],
          fields: tab.fields || [],
          data: [],
        }));
        setDatasheet({
          id: format.id ?? effectiveFormatId ?? 0,
          name: format.name ?? '',
          tabs,
          gridSettings: DEFAULT_GRID_SETTINGS,
        });
        if (effectiveFormatId === tdsFormatIdFromStorage && props.tableFieldId != null && props.globalId != null && typeof sessionStorage !== "undefined") {
          sessionStorage.removeItem(`TDS_PENDING_FORMAT_ID_${props.tableFieldId}_${props.globalId}`);
          sessionStorage.removeItem(`TDS_PENDING_TAB_ID_${props.tableFieldId}_${props.globalId}`);
        }
      })
      .catch((err) => {
        if (!cancelled) console.error("Failed to load table datasheet format", err);
      });
    return () => { cancelled = true; };
  }, [isTableDatasheetResult, props.formatId, props.tableContext, props.context, props.tableFieldId, props.globalId, tdsFormatIdFromStorage, tdsTabIdFromStorage]);

  React.useEffect(() => {
    if (isTableDatasheetResult) return;
    let cancelled = false;
    if (!formatId || !props.context) return;

    loadDatasheetFormat({
      formatId: formatId,
      context: props.context,
      tableContext: props.tableContext,
      globalId: props.globalId,
      tabId: 1,
    })
      .then((response) => {
        if (cancelled) {
          return;
        }

        const format = (response as any)?.format ?? response;

        if (!format || !format.tabsData) {
          setShowNoDefaultFormatDialog(true);
          return;
        }
        const restProperties = (response as any)?.properties;

        let id: number | null = null;
        if (props.context === 1) {
          id = props.globalId || null;
        } else if (props.context === 2) {
          id = props.globalId || null;
        } else {
          id = props.globalId || null;
        }

        const firstTabId = format.tabsData && format.tabsData.length > 0
          ? (format.tabsData[0].id ?? 1)
          : 1;

        const loadDataPromise = id !== null
          ? loadDatasheetData({
              id: id,
              formatId: formatId!,
              tabId: firstTabId,
              context: props.context!,
            }).catch((err) => {
              console.error("Failed to load datasheet data", err);
              return null;
            })
          : Promise.resolve(null);

        return loadDataPromise.then((datasheetData) => {
          if (cancelled) {
            return;
          }

          if (props.globalId != null && typeof sessionStorage !== "undefined") {
            sessionStorage.setItem(
              `TDS_BASE_FORMAT_ID_${props.globalId}`,
              String(formatId!)
            );
          }

          const tabs = Array.isArray(format.tabsData)
            ? format.tabsData.map((tab: any, idx: number) => {
                const tabId = tab.id ?? idx + 1;
                if (tabId === firstTabId && datasheetData) {
                  return {
                    id: tabId,
                    name: tab.name ?? `Tab ${idx + 1}`,
                    texts: mergeDatasheetFormatTextsWithDataTexts(
                      tab.texts as unknown as Array<Record<string, unknown>>,
                      datasheetData.texts as unknown as Array<Record<string, unknown>> | undefined
                    ) as unknown as DatasheetTabDto["texts"],
                    fields: tab.fields || [],
                    data: datasheetData.data || [],
                  };
                } else {
                  return {
                    id: tabId,
                    name: tab.name ?? `Tab ${idx + 1}`,
                    texts: tab.texts || [],
                    fields: tab.fields || [],
                  };
                }
              })
            : [];

          setDatasheet({
            id: format.id ?? formatId ?? 0,
            name: format.name ?? '',
            tabs,
            gridSettings: DEFAULT_GRID_SETTINGS,
          });

          if (
            datasheetLockContext != null &&
            props.globalId != null &&
            props.globalId > 0
          ) {
            if (restProperties && typeof restProperties.lock === "number") {
              if (!cancelled) {
                applyDatasheetLockState({
                  id: props.globalId!,
                  context: datasheetLockContext,
                  acquired: restProperties.lock === 1,
                  lockUser: restProperties.lockUser ?? null,
                });
              }
            } else {
              acquireDatasheetLock({ id: props.globalId, context: datasheetLockContext })
                .then((r) => {
                  if (cancelled) return;
                  applyDatasheetLockState({
                    id: props.globalId!,
                    context: datasheetLockContext,
                    acquired: r.acquired,
                    lockUser: r.lockUser,
                  });
                })
                .catch((err) => {
                  if (!cancelled) {
                    // eslint-disable-next-line no-console
                    console.error("Failed to acquire datasheet lock (after loadDSFormat)", err);
                  }
                });
            }
          }
        });
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("Failed to load datasheet format", err);
          setShowNoDefaultFormatDialog(true);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [formatId, props.context, props.tableContext, isTableDatasheetResult, datasheetLockContext, applyDatasheetLockState]);

  React.useEffect(() => {
    if (isTableDatasheetResult) {
      if (props.formatId && props.formatId > 0) {
        if (formatId !== props.formatId) setFormatId(props.formatId);
        if (store) store.dispatch(setDatasheetFormatPreference(cacheKey, props.formatId));
        setShowNoDefaultFormatDialog(false);
      } else if (!tdsUserPickedFormat.current) {
        setShowNoDefaultFormatDialog(true);
      }
      return;
    }

    if (datasheet.id > 0 && previousGlobalIdRef.current !== undefined && previousGlobalIdRef.current !== props.globalId) {
      return;
    }

    if (cachedFormatId) {
      if (formatId !== cachedFormatId) setFormatId(cachedFormatId);
      setShowNoDefaultFormatDialog(false);
      return;
    }

    if (props.formatId && props.formatId > 0) {
      if (props.formatId !== formatId) setFormatId(props.formatId);
      setShowNoDefaultFormatDialog(false);
      if (store) store.dispatch(setDatasheetFormatPreference(cacheKey, props.formatId));
      return;
    }

    if (formatId) setFormatId(undefined);
    setShowNoDefaultFormatDialog(true);
  }, [isTableDatasheetResult, props.formatId, props.globalId, cachedFormatId, formatId, store, cacheKey, datasheet.id]);

  const handleDatasheetFormatSelect = React.useCallback(
    async (params: { format: FormatFieldItem; folder: DatasheetFormatManagementFolder }) => {
      if (!params?.format?.id) {
        return;
      }
      if (!props.context) {
        console.warn("No context provided; cannot load datasheet format");
        return;
      }

      try {
        const formatData = await loadDatasheetFormat({
          formatId: Number(params.format.id),
          context: props.context,
          tableContext: props.tableContext,
          globalId: props.globalId,
          tabId: 1,
        });

        const format = (formatData as any)?.format ?? formatData;
        if (!format) {
          console.warn("No format data returned from API");
          return;
        }

        if (!format.tabsData) {
          console.warn("No tabsData in format response", format);
          return;
        }

        let id: number | null = null;
        if (props.context === 1) {
          id = props.globalId || null;
          if (!id) {
            console.warn("No globalId provided for individual datasheet");
          }
        } else if (props.context === 2) {
          id = props.globalId || null;
        } else {
          id = props.globalId || null;
        }

        const firstTabId = format.tabsData && format.tabsData.length > 0
          ? (format.tabsData[0].id ?? 1)
          : 1;

        let datasheetData: any = null;
        if (id !== null) {
          try {
            datasheetData = await loadDatasheetData({
              id: id,
              formatId: Number(params.format.id),
              tabId: firstTabId,
              context: props.context,
            });
          } catch (err) {
            console.error("Failed to load datasheet data", err);
          }
        }

        const tabs = Array.isArray(format.tabsData)
          ? format.tabsData.map((tab: any, idx: number) => {
              const tabId = tab.id ?? idx + 1;
              if (tabId === firstTabId && datasheetData) {
                return {
                  id: tabId,
                  name: tab.name ?? `Tab ${idx + 1}`,
                  texts: mergeDatasheetFormatTextsWithDataTexts(
                    tab.texts as unknown as Array<Record<string, unknown>>,
                    datasheetData.texts as unknown as Array<Record<string, unknown>> | undefined
                  ) as unknown as DatasheetTabDto["texts"],
                  fields: tab.fields || [],
                  data: datasheetData.data || [],
                };
              } else {
                return {
                  id: tabId,
                  name: tab.name ?? `Tab ${idx + 1}`,
                  texts: tab.texts || [],
                  fields: tab.fields || [],
                };
              }
            })
          : [];

        setDatasheet({
          id: format.id ?? Number(params.format.id) ?? 0,
          name: format.name ?? '',
          tabs,
          gridSettings: DEFAULT_GRID_SETTINGS,
        });

        if (
          datasheetLockContext != null &&
          props.globalId != null &&
          props.globalId > 0 &&
          !datasheetHeldLockRef.current
        ) {
          const selProperties = (formatData as any)?.properties;
          if (selProperties && typeof selProperties.lock === "number") {
            applyDatasheetLockState({
              id: props.globalId,
              context: datasheetLockContext,
              acquired: selProperties.lock === 1,
              lockUser: selProperties.lockUser ?? null,
            });
          } else {
            try {
              const r = await acquireDatasheetLock({
                id: props.globalId,
                context: datasheetLockContext,
              });
              applyDatasheetLockState({
                id: props.globalId,
                context: datasheetLockContext,
                acquired: r.acquired,
                lockUser: r.lockUser,
              });
            } catch (err) {
              // eslint-disable-next-line no-console
              console.error("Failed to acquire datasheet lock (after format select)", err);
            }
          }
        }

        const nextFormatId = Number(params.format.id);
        setFormatId(nextFormatId);
        setShowNoDefaultFormatDialog(false);
        if (store) {
          store.dispatch(setDatasheetFormatPreference(cacheKey, nextFormatId));
        }
      } catch (err) {
        console.error("Failed to load datasheet format from selector", err);
      }
    },
    [props.context, props.tableContext, props.globalId, cacheKey, store, datasheetLockContext, applyDatasheetLockState]
  );

  const handleTdsFormatSelect = React.useCallback(
    async (params: { format: { id: number; name: string } }) => {
      const selectedFormatId = Number(params.format.id);
      if (!selectedFormatId) return;

      const tableContext = props.tableContext ?? props.context ?? 1;
      try {
        const response = await loadDatasheetFormat({
          formatId: selectedFormatId,
          context: LOAD_DS_FORMAT_TABLE_CONTEXT,
          tableContext: tableContext,
          globalIdForTds: props.globalId,
          tabId: 1,
        });

        const format = (response as any)?.format ?? response;
        if (!format || !format.tabsData) return;

        const tabs = format.tabsData.map((tab: any, idx: number) => ({
          id: tab.id ?? idx + 1,
          name: tab.name ?? `Tab ${idx + 1}`,
          texts: tab.texts || [],
          fields: tab.fields || [],
          data: [],
        }));

        tdsUserPickedFormat.current = true;
        setDatasheet({
          id: format.id ?? selectedFormatId,
          name: format.name ?? '',
          tabs,
          gridSettings: DEFAULT_GRID_SETTINGS,
        });
        setShowNoDefaultFormatDialog(false);
      } catch (err) {
        console.error("Failed to load TDS format from selector", err);
      }
    },
    [props.tableContext, props.context, props.globalId]
  );

  const inFlightTabDataLoadsRef = React.useRef<Record<number, Promise<void> | undefined>>({});
  const loadedTabIdsRef = React.useRef<Set<number>>(new Set());

  React.useEffect(() => {
    loadedTabIdsRef.current = new Set();
  }, [formatId, props.globalId, props.context]);

  const previousGlobalIdRef = React.useRef<number | undefined>(undefined);

  const loadTabData = React.useCallback(
    async (tabId: number, options?: { forceRefresh?: boolean; silent?: boolean }) => {
      const formatIdToUse = datasheet.id || formatId || props.formatId;

      const globalId = props.globalId;
      const context = props.context;
      const tableFieldId = props.tableFieldId;

      if (formatIdToUse == null || !globalId) {
        return;
      }

      const tableContext = props.tableContext ?? props.context ?? 1;

      if (isTableDatasheetResult && tableFieldId) {
        try {
          if (!options?.silent) setIsLoadingData(true);
          const selectedRrn = tdsRowRrns[Math.max(0, Math.min(tdsCurrentRowIndex, tdsRowRrns.length - 1))];
          if (selectedRrn == null || selectedRrn <= 0) {
            setDatasheet((prev) => {
              const updatedTabs = prev.tabs.map((tab) =>
                tab.id === tabId ? ({ ...(tab as any), data: [], tableRows: [] } as any) : tab
              );
              return { ...prev, tabs: updatedTabs };
            });
            setIsLoadingData(false);
            return;
          }

          const selectedRowResponse = await loadTableDatasheetData({
            id: globalId,
            formatId: formatIdToUse,
            tabId,
            tableFieldId,
            context: tableContext,
            rrn: selectedRrn,
          });
          const selectedRowData =
            (selectedRowResponse as any)?.rowData ??
            (selectedRowResponse as any)?.data;
          const activeRowData = Array.isArray(selectedRowData?.data) ? selectedRowData.data : [];

          setDatasheet((prev) => {
            const updatedTabs = prev.tabs.map((tab) => {
              if (tab.id === tabId) {
                return {
                  ...tab,
                  data: activeRowData,
                  tableRows: selectedRowData ? [selectedRowData] : [],
                } as any;
              }
              return tab;
            });
            return { ...prev, tabs: updatedTabs };
          });
        } catch (err) {
          console.error("Failed to load table datasheet data for tab", tabId, err);
        } finally {
          setIsLoadingData(false);
        }
        return;
      }

      if (!context) return;

      const existingLoad = inFlightTabDataLoadsRef.current[tabId];
      if (existingLoad) {
        if (!options?.silent) setIsLoadingData(true);
        return existingLoad;
      }

      const alreadyLoaded = loadedTabIdsRef.current.has(tabId);

      const tabForCheck = datasheet.tabs?.find((t) => t.id == tabId) as
          | (DatasheetTabDto & { data?: unknown[] })
          | undefined;
      const tabAlreadyLoadedData =
          tabForCheck?.data !== undefined && Array.isArray(tabForCheck.data);
      if ((alreadyLoaded || tabAlreadyLoadedData) && !options?.forceRefresh) {
        return;
      }

      const loadPromise = (async () => {
        try {
          if (!options?.silent) setIsLoadingData(true);
        const dataResponse = await loadDatasheetData({
          id: globalId,
          formatId: formatIdToUse,
          tabId,
          context,
        });

        setDatasheet((prev) => {
          const updatedTabs = prev.tabs.map((tab) => {
            if (tab.id === tabId) {
              loadedTabIdsRef.current.add(tabId);
              return {
                ...tab,
                texts: mergeDatasheetFormatTextsWithDataTexts(
                  tab.texts as unknown as Array<Record<string, unknown>>,
                  dataResponse?.texts as unknown as Array<Record<string, unknown>> | undefined
                ) as unknown as DatasheetTabDto["texts"],
                data: dataResponse?.data || [],
              };
            }
            return tab;
          });

          return {
            ...prev,
            tabs: updatedTabs,
          };
        });
        } catch (err) {
          console.error("Failed to load datasheet data for tab", tabId, err);
        } finally {
          inFlightTabDataLoadsRef.current[tabId] = undefined;
          setIsLoadingData(false);
        }
      })();

      inFlightTabDataLoadsRef.current[tabId] = loadPromise;
      return loadPromise;
    },
    [datasheet.id, formatId, props.formatId, props.context, props.globalId, props.tableContext, props.tableFieldId, isTableDatasheetResult, tdsRowRrns, tdsCurrentRowIndex]
  );

  const loadTabFormat = React.useCallback(
    async (tabId: number) => {
      const formatIdToUse = datasheet.id || formatId || props.formatId;
      if (formatIdToUse == null || !props.context) {
        return;
      }
      const tab = datasheet.tabs?.find((t) => t.id === tabId);
      if (tab?.fields != null && Array.isArray(tab.fields) && tab.fields.length > 0) {
        return;
      }
      try {
        setIsLoadingData(true);
        const response = await getNotLoadedDatasheetComponents({
          formatId: formatIdToUse,
          tabs: [tabId],
          ...(isTableDatasheetResult ? { isTDS: true } : {}),
        });
        const format = (response as any)?.tabsData != null ? response : null;
        if (!format?.tabsData?.length) {
          return;
        }
        setDatasheet((prev) => {
          const updatedTabs = prev.tabs.map((t) => {
            const loaded = format.tabsData.find((tt: any) => (tt.id ?? 0) === t.id);
            if (!loaded || t.id !== tabId) return t;
            return {
              ...t,
              fields: loaded.fields ?? t.fields ?? [],
              texts: loaded.texts ?? t.texts ?? [],
            };
          });
          return { ...prev, tabs: updatedTabs };
        });
      } catch (err) {
        console.error("Failed to load tab format", tabId, err);
      } finally {
        setIsLoadingData(false);
      }
    },
    [datasheet.id, datasheet.tabs, formatId, props.formatId, props.context, isTableDatasheetResult]
  );

  const tdsTotalRows = tdsRowRrns.length;
  const activeTdsRrn = React.useMemo(() => {
    if (!isTableDatasheetResult || tdsTotalRows === 0) return undefined;
    return tdsRowRrns[Math.max(0, Math.min(tdsCurrentRowIndex, tdsTotalRows - 1))];
  }, [isTableDatasheetResult, tdsRowRrns, tdsCurrentRowIndex, tdsTotalRows]);

  const datasheetEditLocked = Boolean(
    datasheetLockContext != null &&
      props.globalId != null &&
      props.globalId > 0 &&
      !datasheetLockHeld
  );

  const notifyTableSyncMarkerConsumed = React.useCallback(() => {
    if (!tableSyncMarkerKey || typeof sessionStorage === "undefined") {
      return;
    }
    const m = sessionStorage.getItem(tableSyncMarkerKey);
    if (m != null) {
      seenTableSyncMarkerRef.current = m;
    }
  }, [tableSyncMarkerKey]);

  const datasheetController = useDatasheetController({
    datasheet,
    onDatasheetChange: setDatasheet,
    datasheetEditLocked,
    context: props.context,
    tableContext: props.tableContext,
    globalId: props.globalId,
    tableFieldId: props.tableFieldId,
    currentTdsRrn: activeTdsRrn,
    loadTabData,
    loadTabFormat,
    formatOnly: props.formatOnly,
    preventTableDataPreservationRef,
    initialTabId: getCachedTabIdFromStore(),
    onTabChange: persistTabPreference,
    notifyTableSyncMarkerConsumed,
  });

  const initialTabRestoredRef = React.useRef(false);

  React.useEffect(() => {
    initialTabRestoredRef.current = false;
  }, [props.globalId]);

  React.useEffect(() => {
    if (initialTabRestoredRef.current) return;
    if (isLoadingData) return;
    if (!datasheet.id || !datasheet.tabs.length) return;

    const persistedTabId = getCachedTabIdFromStore();
    if (persistedTabId == null) return;
    if (Number(persistedTabId) === Number(datasheetController.currentTabId)) return;
    if (!datasheet.tabs.some((t) => t.id === persistedTabId)) return;

    initialTabRestoredRef.current = true;
    datasheetController.handleSelectedTabChange(persistedTabId);
  }, [isLoadingData, datasheet.id, datasheet.tabs, getCachedTabIdFromStore, datasheetController.currentTabId]);

  const refreshActiveTabData = React.useCallback((options?: { silent?: boolean }) => {
    if (isTableDatasheetResult) return;
    if (!datasheet.id || !props.globalId || !datasheetController.currentTabId) return;
    preventTableDataPreservationRef.current = true;
    loadTabData(Number(datasheetController.currentTabId), { forceRefresh: true, silent: options?.silent });
  }, [isTableDatasheetResult, datasheet.id, props.globalId, datasheetController.currentTabId, loadTabData]);

  React.useEffect(() => {
    if (isTableDatasheetResult) return;
    if (props.globalId == null) return;
    const hasFormatLoaded = datasheet.id > 0 && Array.isArray(datasheet.tabs) && datasheet.tabs.length > 0;
    if (!hasFormatLoaded) {
      previousGlobalIdRef.current = props.globalId;
      return;
    }
    const prevId = previousGlobalIdRef.current;
    previousGlobalIdRef.current = props.globalId;
    if (prevId === undefined) return;
    if (prevId === props.globalId) return;

    inFlightTabDataLoadsRef.current = {};
    setIsLoadingData(true);
    setDatasheet((prev) => ({
      ...prev,
      tabs: prev.tabs.map((tab) => {
        const { data, ...rest } = tab as any;
        return rest;
      }),
    }));
    const tabIdToLoad = datasheetController.currentTabId ?? datasheet.tabs[0]?.id;
    if (tabIdToLoad != null) {
      loadTabData(Number(tabIdToLoad), { forceRefresh: true }).finally(() => setIsLoadingData(false));
    } else {
      setIsLoadingData(false);
    }
  }, [props.globalId, isTableDatasheetResult, datasheet.id, datasheet.tabs, datasheetController.currentTabId, loadTabData]);

  const hasUnseenTableSyncMarker = React.useCallback(() => {
    if (!tableSyncMarkerKey || typeof sessionStorage === "undefined") {
      return false;
    }
    const marker = sessionStorage.getItem(tableSyncMarkerKey);
    if (!marker || marker === seenTableSyncMarkerRef.current) {
      return false;
    }
    seenTableSyncMarkerRef.current = marker;
    return true;
  }, [tableSyncMarkerKey]);

  const syncTdsRowsFromStorage = React.useCallback(() => {
    if (!isTableDatasheetResult || !props.tableFieldId || props.globalId == null) {
      return;
    }
    if (typeof sessionStorage === "undefined") {
      return;
    }
    const key = `TDS_PENDING_ROW_RRNS_${props.tableFieldId}_${props.globalId}`;
    const raw = sessionStorage.getItem(key);
    if (!raw) {
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return;
      }
      const rrns = parseRrnsPassthrough(parsed as Array<number | string | null | undefined>);
      if (rrns.length > 0) {
        setTdsRowRrns(rrns);
      }
    } catch {
    }
  }, [isTableDatasheetResult, props.tableFieldId, props.globalId]);

  React.useEffect(() => {
    if (isTableDatasheetResult) return;

    const handleWindowFocus = () => {
      refreshActiveTabData();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshActiveTabData();
      }
    };

    window.addEventListener("focus", handleWindowFocus);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("focus", handleWindowFocus);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isTableDatasheetResult, refreshActiveTabData]);

  React.useEffect(() => {
    if (isTableDatasheetResult) return;

    const handleWindowClick = () => {
      if (!hasUnseenTableSyncMarker()) {
        return;
      }
      refreshActiveTabData({ silent: true });
    };

    window.addEventListener("click", handleWindowClick);

    return () => {
      window.removeEventListener("click", handleWindowClick);
    };
  }, [isTableDatasheetResult, hasUnseenTableSyncMarker, refreshActiveTabData]);

  React.useEffect(() => {
    if (!isTableDatasheetResult) return;

    const handleWindowClick = () => {
      if (!hasUnseenTableSyncMarker()) {
        return;
      }
      syncTdsRowsFromStorage();
      if (datasheetController.currentTabId != null) {
        loadTabData(Number(datasheetController.currentTabId));
      }
    };

    window.addEventListener("click", handleWindowClick);

    return () => {
      window.removeEventListener("click", handleWindowClick);
    };
  }, [isTableDatasheetResult, hasUnseenTableSyncMarker, syncTdsRowsFromStorage, datasheetController.currentTabId, loadTabData]);

  React.useEffect(() => {
    if (!isTableDatasheetResult || !datasheet.id || !props.globalId || !datasheetController.currentTabId) return;
    if (isTableDatasheetResult && tdsRowRrns.length === 0) return;
    if (
      isTableDatasheetResult &&
      !hasAppliedInitialTdsRowRef.current &&
      props.initialRrn != null &&
      props.initialRrn > 0
    ) {
      const initialIdx = tdsRowRrns.findIndex((rrn) => rrn === props.initialRrn);
      if (initialIdx >= 0 && tdsCurrentRowIndex !== initialIdx) {
        setTdsCurrentRowIndex(initialIdx);
        return;
      }
      hasAppliedInitialTdsRowRef.current = true;
    } else if (isTableDatasheetResult && !hasAppliedInitialTdsRowRef.current) {
      hasAppliedInitialTdsRowRef.current = true;
    }
    loadTabData(Number(datasheetController.currentTabId));
  }, [
    isTableDatasheetResult,
    datasheet.id,
    props.globalId,
    props.initialRrn,
    datasheetController.currentTabId,
    loadTabData,
    tdsRowRrns,
    tdsCurrentRowIndex,
  ]);

  React.useEffect(() => {
    setTdsCurrentRowIndex((prev) => {
      if (!tdsRowRrns.length) return 0;
      return Math.max(0, Math.min(prev, tdsRowRrns.length - 1));
    });
  }, [tdsRowRrns]);

  const tdsCurrentRowLabel =
    tdsTotalRows > 0
      ? `Row ${Math.min(tdsCurrentRowIndex + 1, tdsTotalRows)} of ${tdsTotalRows}`
      : "Row 0 of 0";

  const handleTdsFirstRow = React.useCallback(() => setTdsCurrentRowIndex(0), []);
  const handleTdsPrevRow = React.useCallback(() => {
    setTdsCurrentRowIndex((prev) => Math.max(0, prev - 1));
  }, []);
  const handleTdsNextRow = React.useCallback(() => {
    if (tdsTotalRows <= 0) return;
    setTdsCurrentRowIndex((prev) => Math.min(tdsTotalRows - 1, prev + 1));
  }, [tdsTotalRows]);
  const handleTdsLastRow = React.useCallback(() => {
    if (tdsTotalRows <= 0) return;
    setTdsCurrentRowIndex(Math.max(0, tdsTotalRows - 1));
  }, [tdsTotalRows]);

  const handleTdsAddRow = React.useCallback(async () => {
    if (!isTableDatasheetResult || !props.globalId || !props.tableFieldId) return;
    const rowContext = props.tableContext ?? props.context;
    if (!rowContext) return;
    try {
      const row = await addDatasheetTableRow({
        globalId: props.globalId,
        context: rowContext,
        tableId: props.tableFieldId,
        systemField: false,
        copyFromRrn: -1,
      });
      if (typeof row?.rrn === "number" && row.rrn > 0) {
        setTdsRowRrns((prev) => {
          const next = [...prev, row.rrn];
          const newIndex = next.findIndex((rrn) => rrn === row.rrn);
          setTdsCurrentRowIndex(newIndex >= 0 ? newIndex : Math.max(0, next.length - 1));
          if (typeof sessionStorage !== "undefined" && props.tableFieldId && props.globalId != null) {
            sessionStorage.setItem(
              `TDS_PENDING_ROW_RRNS_${props.tableFieldId}_${props.globalId}`,
              JSON.stringify(next)
            );
          }
          return next;
        });
        if (tableSyncMarkerKey && typeof sessionStorage !== "undefined") {
          sessionStorage.setItem(tableSyncMarkerKey, String(Date.now()));
        }
      } else {
        if (datasheetController.currentTabId != null) {
          await loadTabData(Number(datasheetController.currentTabId));
        }
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error("Failed to add TDS row", error);
    }
  }, [isTableDatasheetResult, props.globalId, props.tableFieldId, props.tableContext, props.context, datasheetController.currentTabId, loadTabData, tableSyncMarkerKey]);

  const printableDatasheetHolderId = React.useMemo(() => `${PRINTABLE_RESULT_DATASHEET_ELEMENT_ID}-${generateId()}`, []);

  const getDatasheetFolderFormats = React.useCallback((params: GetDatasheetFolderFormats) => {
    const contextValue = Number(params.context);
    const context = (contextValue && contextValue !== 0) ? contextValue : (props.context ?? 1);
    
    return getDatasheetFolderFormatsBase({
      ...params,
      context: context as any,
    }).then((fields) => {
      return fields.map((x): FormatFieldItem => {
        return {
          id: x.id,
          name: x.name,
          folderId: x.folderid,
          imageURL: "gif/format.gif",
          description: ''
        };
      });
    });
  }, [props.context]);

  const getFoldersForDatasheet = React.useCallback(() => {
    const context = props.context ?? 1;
    const tds = false;
    const parentId = 0;
    return getDatasheetFolders(context, tds, parentId).then((x) => [
      {
        imageURL: "gif/dbenthi.gif",
        imageURLSelected: "gif/dbenthi.gif",
        name: trDts("Datasheet Format Folders"),
        id: 0,
        children: x.map((folder): DatasheetFormatManagementFolder => {
          return {
            name: folder.name,
            parentid: folder.parentid,
            id: folder.id,
            context: folder.context,
            fieldcontext: folder.fieldcontext,
            imageURL: "gif/folder_open.gif",
            imageURLSelected: "gif/folder_open.gif",
          };
        }),
      },
    ] as Array<DatasheetFormatManagementFolder>);
  }, [props.context]);

  const datasheetPrintController = useDatasheetPrintController({
    printableDatasheetHolderId: printableDatasheetHolderId,
    printableDatasheetFieldId: PRINTABLE_DATASHEET_FIELD_ID
  });

  return {
    datasheet,
    datasheetEditLocked,
    datasheetLockBlocked,
    datasheetLockUserName,
    isLoadingData,
    isTableDatasheetResult,
    tdsCurrentRowLabel,
    tdsCanMovePrev: tdsCurrentRowIndex > 0,
    tdsCanMoveNext: tdsCurrentRowIndex < Math.max(0, tdsTotalRows - 1),
    handleTdsFirstRow,
    handleTdsPrevRow,
    handleTdsNextRow,
    handleTdsLastRow,
    handleTdsAddRow,
    handleUpnChange,
    handleDatasheetFormatSelect,
    handleTdsFormatSelect,
    printableDatasheetHolderId,
    getDatasheetFolderFormats,
    getFoldersForDatasheet,
    currentUpn,
    showNoDefaultFormatDialog,
    setShowNoDefaultFormatDialog,
    ...datasheetPrintController,
    ...datasheetController,
  };
};